import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { producerReferralLink } from "../../lib/urls";
import { toast } from "sonner";
import {
    Gift, Copy, Share2, Mail, MessageCircle, Percent, Users,
    CheckCircle2, TrendingDown, Clock,
} from "lucide-react";

const STATUS_META = {
    pending: { label: "Pendente", color: "bg-gold-soft/20 text-gold-deep" },
    awaiting_sale: { label: "Aguardando venda", color: "bg-wine/10 text-wine" },
    approved: { label: "Aprovado", color: "bg-vineyard/15 text-vineyard" },
    qualified: { label: "Qualificado", color: "bg-vineyard text-cream" },
    rejected: { label: "Rejeitado", color: "bg-destructive/15 text-destructive" },
};

export default function VendorReferrals() {
    const [data, setData] = useState(null);

    useEffect(() => {
        api.get("/vendor/referral").then((r) => setData(r.data)).catch(() => {});
    }, []);

    if (!data) return <p className="text-wine-dark/60 label-overline">A carregar…</p>;

    const code = data.code || "—";
    const url = producerReferralLink(data.code) || data.url || "";
    const share = (fn) => () => fn();
    const copyLink = share(async () => {
        await navigator.clipboard.writeText(url);
        toast.success("Link copiado");
    });
    const copyCode = share(async () => {
        await navigator.clipboard.writeText(code);
        toast.success("Código copiado");
    });
    const msg = `Junte-se ao Caseirinho como produtor através do meu convite: ${url}`;
    const wa = `https://wa.me/?text=${encodeURIComponent(msg)}`;
    const mail = `mailto:?subject=${encodeURIComponent("Convite Caseirinho")}&body=${encodeURIComponent(msg)}`;
    const nativeShare = share(async () => {
        if (navigator.share) {
            try { await navigator.share({ title: "Caseirinho", text: msg, url }); } catch { /* cancelled */ }
        } else { copyLink(); }
    });

    return (
        <section className="space-y-6" data-testid="vendor-referrals">
            <div>
                <span className="label-overline text-wine/60 inline-flex items-center gap-1.5">
                    <Gift className="h-3 w-3" /> Programa de indicações
                </span>
                <h1 className="font-heading text-3xl text-wine-dark">Indique um Produtor</h1>
                <p className="text-sm text-wine-dark/60 mt-1">{data.message}</p>
            </div>

            {/* Benefit card */}
            <div className="bg-wine-dark text-cream rounded-md p-6" data-testid="benefit-card">
                <span className="label-overline text-gold-soft inline-flex items-center gap-1.5">
                    <TrendingDown className="h-3.5 w-3.5" /> Benefício por indicações
                </span>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-4">
                    <Metric label="Comissão normal" value={`${data.standard_fee}%`} light />
                    <Metric label="Sua comissão atual" value={`${data.current_fee}%`} highlight />
                    <Metric label="Benefício restante" value={`${data.remaining_days} dias`} light />
                    <Metric label="Produtores indicados" value={data.stats.indicated} light />
                    <Metric label="Economia obtida" value={`€${data.stats.savings.toFixed(2)}`} light />
                </div>
            </div>

            {/* Share card */}
            <div className="bg-white border border-gold-soft/30 rounded-md p-6 space-y-4">
                {!data.approved && (
                    <div className="bg-gold-soft/15 text-gold-deep text-sm rounded-md px-4 py-3">
                        O seu código de indicação fica ativo assim que a sua quinta for aprovada pela equipa Caseirinho.
                    </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <span className="label-overline text-wine-dark/65 text-[10px] block mb-1.5">O seu código</span>
                        <div className="flex items-center gap-2">
                            <code className="flex-1 bg-cream border border-wine-dark/15 rounded-md px-3 py-2.5 text-sm font-mono text-wine-dark" data-testid="referral-code">{code}</code>
                            <button onClick={copyCode} className="btn-wine-outline text-xs px-3 py-2.5" data-testid="copy-code"><Copy className="h-4 w-4" /></button>
                        </div>
                    </div>
                    <div>
                        <span className="label-overline text-wine-dark/65 text-[10px] block mb-1.5">O seu link</span>
                        <div className="flex items-center gap-2">
                            <input readOnly value={url} className="flex-1 bg-cream border border-wine-dark/15 rounded-md px-3 py-2.5 text-sm text-wine-dark truncate" data-testid="referral-url" />
                            <button onClick={copyLink} className="btn-wine-outline text-xs px-3 py-2.5" data-testid="copy-link"><Copy className="h-4 w-4" /></button>
                        </div>
                    </div>
                </div>
                <div className="flex flex-wrap gap-2 pt-1">
                    <button onClick={copyLink} className="inline-flex items-center gap-2 text-xs label-overline bg-wine text-cream px-4 py-2.5 rounded-md hover:bg-wine-dark" data-testid="share-copy"><Copy className="h-3.5 w-3.5" /> Copiar link</button>
                    <a href={wa} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-xs label-overline bg-vineyard text-cream px-4 py-2.5 rounded-md hover:opacity-90" data-testid="share-whatsapp"><MessageCircle className="h-3.5 w-3.5" /> WhatsApp</a>
                    <a href={mail} className="inline-flex items-center gap-2 text-xs label-overline border border-wine-dark/20 text-wine px-4 py-2.5 rounded-md hover:border-wine" data-testid="share-email"><Mail className="h-3.5 w-3.5" /> Email</a>
                    <button onClick={nativeShare} className="inline-flex items-center gap-2 text-xs label-overline border border-wine-dark/20 text-wine px-4 py-2.5 rounded-md hover:border-wine" data-testid="share-native"><Share2 className="h-3.5 w-3.5" /> Partilhar</button>
                </div>
            </div>

            {/* Referrals table */}
            <div className="bg-white border border-gold-soft/30 rounded-md overflow-hidden">
                <div className="px-5 py-4 border-b border-wine-dark/10">
                    <h2 className="font-heading text-xl text-wine-dark inline-flex items-center gap-2"><Users className="h-4 w-4 text-wine" /> Produtores indicados</h2>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left label-overline text-wine-dark/60 border-b border-wine-dark/10 bg-cream/40">
                                <th className="py-3 px-4">Produtor indicado</th>
                                <th>Data</th>
                                <th>Estado</th>
                                <th>Primeira venda</th>
                                <th>Recompensa</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.referrals.map((r) => {
                                const meta = STATUS_META[r.status] || STATUS_META.pending;
                                return (
                                    <tr key={r.id} className="border-t border-wine-dark/5" data-testid={`referral-row-${r.id}`}>
                                        <td className="py-3 px-4 text-wine-dark">{r.referred_name || "—"}</td>
                                        <td className="text-xs text-wine-dark/60">{new Date(r.created_at).toLocaleDateString("pt-PT")}</td>
                                        <td><span className={`inline-flex items-center label-overline px-2 py-1 rounded text-[10px] ${meta.color}`}>{meta.label}</span></td>
                                        <td className="text-xs text-wine-dark/70">{r.qualified_at ? new Date(r.qualified_at).toLocaleDateString("pt-PT") : "—"}</td>
                                        <td className="text-xs">
                                            {r.status === "qualified"
                                                ? <span className="text-vineyard inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> +{data.program.benefit_days} dias a {data.program.promo_fee}%</span>
                                                : <span className="text-wine-dark/40 inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> A aguardar</span>}
                                        </td>
                                    </tr>
                                );
                            })}
                            {data.referrals.length === 0 && (
                                <tr><td colSpan="5" className="text-center py-10 text-wine-dark/60">Ainda sem indicações. Partilhe o seu link para começar.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </section>
    );
}

function Metric({ label, value, highlight, light }) {
    return (
        <div>
            <span className={`label-overline text-[9px] ${highlight ? "text-gold-soft" : "text-cream/50"}`}>{label}</span>
            <p className={`font-heading mt-0.5 ${highlight ? "text-3xl text-gold-soft" : "text-2xl text-cream"}`}>{value}</p>
        </div>
    );
}
